import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Avatar,
  Stack,
  Button,
  Link,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  ChatBubbleOutline,
  Business,
  Language,
  Launch,
  CheckCircle,
  Schedule,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Contact() {
  const { isLight } = useThemeMode();
  const { ref: contactRef, revealStyle, stagger } = useScrollReveal();
  const [copiedKey, setCopiedKey] = useState(null);

  const handleCopy = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }, []);

  const primaryColor = isLight ? '#6366f1' : '#10b981';

  const contactMethods = [
    {
      type: 'General Inquiries',
      icon: <Email sx={{ fontSize: '1.5rem' }} />,
      primary: 'contact@alphanetix.io',
      secondary: 'Response within 2 hours',
      action: 'mailto:contact@alphanetix.io',
      features: ['Technical Questions', 'Product Information'],
    },
    {
      type: 'Bespoke Services',
      icon: <Business sx={{ fontSize: '1.5rem' }} />,
      primary: 'info@bitknoxltd.com',
      secondary: 'Custom development solutions',
      action: 'mailto:info@bitknoxltd.com',
      features: ['Software Development', 'Consultation'],
    },
    {
      type: 'Phone Support',
      icon: <Phone sx={{ fontSize: '1.5rem' }} />,
      primary: '(855) 314-1529',
      secondary: 'Toll-free US number',
      action: 'tel:+18553141529',
      features: ['Immediate Support', 'Sales Help'],
    },
    {
      type: 'Partner Network',
      icon: <Language sx={{ fontSize: '1.5rem' }} />,
      primary: 'BitKnox Ltd',
      secondary: 'Technology partner',
      action: 'https://bitknoxltd.com',
      features: ['Technology Partnership'],
    },
  ];

  const offices = [
    {
      country: 'Pakistan',
      city: 'Lahore',
      address:
        'Suite 207, 2nd Floor, Haly Tower, DHA Phase 2, Lahore',
      phone: '+92 42 34551419',
      icon: 'PK',
      timezone: 'PKT (UTC+5)',
      hours: '9 AM – 6 PM',
    },
    {
      country: 'United States',
      city: 'New Jersey',
      address:
        '309 Fellowship Rd, Suite 200, Mt Laurel Township, NJ 08054',
      phone: '(209) 313-2261',
      icon: 'US',
      timezone: 'EST (UTC-5)',
      hours: '9 AM – 5 PM',
    },
    {
      country: 'Saudi Arabia',
      city: 'Riyadh',
      address:
        '3025 Abi Shuja, King Fahad District, Riyadh 12283',
      phone: '+966 56 682 8360',
      icon: 'KSA',
      timezone: 'AST (UTC+3)',
      hours: '8 AM – 4 PM',
    },
  ];

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(45deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container ref={contactRef} maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, ...revealStyle(0) }}>
          <Chip
            icon={<ChatBubbleOutline sx={{ fontSize: '1rem' }} />}
            label="GET IN TOUCH"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(16, 185, 129, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(16, 185, 129, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': {
                color: isLight ? '#6366f1' : '#10b981',
              },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Global{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5, #6366f1, #818cf8)'
                  : 'linear-gradient(135deg, #10b981, #0ea5e9, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Support
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 550,
              mx: 'auto',
            }}
          >
            Connect with our global team of experts across three continents to
            transform your business.
          </Typography>
        </Box>

        {/* Contact Methods — 4-column grid */}
        <Grid container spacing={2.5} sx={{ mb: { xs: 4, md: 6 } }}>
          {contactMethods.map((method, index) => (
            <Grid item xs={12} sm={6} md={3} key={index} sx={{ ...stagger(index, 200) }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: isLight
                    ? 'rgba(255, 255, 255, 0.4)'
                    : 'rgba(16, 185, 129, 0.06)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : `1px solid ${primaryColor}18`,
                  borderRadius: 3,
                  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: `${primaryColor}40`,
                    boxShadow: `0 12px 32px ${primaryColor}12`,
                    '& .contact-icon': {
                      animation: 'icon-pop 0.4s ease forwards',
                    },
                  },
                }}
              >
                <Avatar
                  className="contact-icon"
                  sx={{
                    width: 44,
                    height: 44,
                    background: `${primaryColor}12`,
                    color: primaryColor,
                    border: `1.5px solid ${primaryColor}25`,
                    mb: 2,
                  }}
                >
                  {method.icon}
                </Avatar>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isLight ? '#1e293b' : '#ffffff',
                    mb: 0.5,
                    fontSize: '0.9rem',
                  }}
                >
                  {method.type}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: primaryColor,
                      fontSize: '0.85rem',
                    }}
                  >
                    {method.primary}
                  </Typography>
                  {!method.action.startsWith('http') && (
                    <Tooltip
                      title={copiedKey === `method-${index}` ? 'Copied!' : 'Copy'}
                      arrow
                      placement="top"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(method.primary, `method-${index}`)}
                        sx={{
                          p: 0.4,
                          color: copiedKey === `method-${index}` ? '#22c55e' : `${primaryColor}60`,
                          transition: 'all 0.2s ease',
                          '&:hover': { color: primaryColor, background: `${primaryColor}10` },
                        }}
                      >
                        {copiedKey === `method-${index}` ? (
                          <Check sx={{ fontSize: '0.85rem' }} />
                        ) : (
                          <ContentCopy sx={{ fontSize: '0.85rem' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    color: isLight
                      ? 'rgba(15, 23, 42, 0.6)'
                      : 'rgba(248, 250, 252, 0.6)',
                    mb: 2,
                    fontSize: '0.75rem',
                  }}
                >
                  {method.secondary}
                </Typography>

                <Stack spacing={0.5} sx={{ mb: 2, flex: 1 }}>
                  {method.features.map((feature, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CheckCircle
                        sx={{ fontSize: '0.75rem', color: primaryColor }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLight
                            ? 'rgba(15, 23, 42, 0.7)'
                            : 'rgba(248, 250, 252, 0.7)',
                          fontSize: '0.7rem',
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  variant="outlined"
                  fullWidth
                  component={method.action.startsWith('http') ? 'a' : Link}
                  href={method.action}
                  target={
                    method.action.startsWith('http') ? '_blank' : undefined
                  }
                  endIcon={
                    method.action.startsWith('http') ? (
                      <Launch sx={{ fontSize: '0.8rem' }} />
                    ) : null
                  }
                  size="small"
                  sx={{
                    borderColor: `${primaryColor}30`,
                    color: primaryColor,
                    borderRadius: 2,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 0.75,
                    transition: 'all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                      borderColor: primaryColor,
                      background: `${primaryColor}08`,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0) scale(0.96)',
                      transition: 'transform 0.1s ease',
                    },
                  }}
                >
                  {method.action.startsWith('http')
                    ? 'Visit Website'
                    : method.action.startsWith('tel')
                    ? 'Call Now'
                    : 'Send Email'}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Office Locations — compact */}
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 800,
            color: isLight ? '#0f172a' : '#ffffff',
            mb: 3,
            fontSize: { xs: '1.3rem', md: '1.6rem' },
            textAlign: 'center',
          }}
        >
          Our Global Offices
        </Typography>

        <Grid container spacing={2.5}>
          {offices.map((office, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ ...stagger(index, 600) }}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: isLight
                    ? 'rgba(255, 255, 255, 0.4)'
                    : 'rgba(16, 185, 129, 0.05)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : `1px solid ${primaryColor}15`,
                  borderRadius: 3,
                  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.05)' : 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: `${primaryColor}35`,
                    boxShadow: `0 12px 32px ${primaryColor}10`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        letterSpacing: '0.05em',
                        color: isLight ? '#6366f1' : '#38bdf8',
                        background: isLight ? 'rgba(99,102,241,0.08)' : 'rgba(56,189,248,0.1)',
                        borderRadius: '6px',
                        px: 1.2,
                        py: 0.5,
                        minWidth: 36,
                        textAlign: 'center',
                        lineHeight: 1.4,
                      }}
                    >
                      {office.icon}
                    </Typography>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: isLight ? '#0f172a' : '#ffffff',
                          lineHeight: 1.2,
                        }}
                      >
                        {office.country}
                      </Typography>
                      <Chip
                        label={office.city}
                        size="small"
                        sx={{
                          background: primaryColor,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 20,
                        }}
                      />
                    </Box>
                  </Box>

                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <LocationOn
                        sx={{
                          color: primaryColor,
                          fontSize: '1rem',
                          mt: 0.2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLight
                            ? 'rgba(15, 23, 42, 0.75)'
                            : 'rgba(248, 250, 252, 0.75)',
                          lineHeight: 1.4,
                          fontSize: '0.75rem',
                        }}
                      >
                        {office.address}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Phone
                        sx={{ color: primaryColor, fontSize: '1rem' }}
                      />
                      <Link
                        href={`tel:${office.phone.replace(/\D/g, '')}`}
                        underline="hover"
                        sx={{
                          color: primaryColor,
                          fontWeight: 600,
                          fontSize: '0.8rem',
                        }}
                      >
                        {office.phone}
                      </Link>
                      <Tooltip
                        title={copiedKey === `office-${index}` ? 'Copied!' : 'Copy'}
                        arrow
                        placement="top"
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(office.phone, `office-${index}`)}
                          sx={{
                            p: 0.3,
                            color: copiedKey === `office-${index}` ? '#22c55e' : `${primaryColor}50`,
                            transition: 'all 0.2s ease',
                            '&:hover': { color: primaryColor, background: `${primaryColor}10` },
                          }}
                        >
                          {copiedKey === `office-${index}` ? (
                            <Check sx={{ fontSize: '0.8rem' }} />
                          ) : (
                            <ContentCopy sx={{ fontSize: '0.8rem' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Schedule
                        sx={{ color: primaryColor, fontSize: '1rem' }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isLight
                            ? 'rgba(15, 23, 42, 0.7)'
                            : 'rgba(248, 250, 252, 0.7)',
                          fontSize: '0.75rem',
                        }}
                      >
                        {office.hours} ({office.timezone})
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom summary */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mt: 4,
            color: isLight
              ? 'rgba(15, 23, 42, 0.5)'
              : 'rgba(248, 250, 252, 0.5)',
            fontSize: '0.85rem',
          }}
        >
          Response within 2 hours &bull; Free consultation &bull; 24/7 global
          coverage &bull;{' '}
          <Box
            component="span"
            sx={{
              color: primaryColor,
              fontWeight: 600,
            }}
          >
            99.9% uptime SLA
          </Box>
        </Typography>
      </Container>
    </Box>
  );
}

export default Contact;
